import React, { useEffect } from 'react'
import cx from 'clsx'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { IconMenu2, IconMessage2, IconRocket } from '@tabler/icons-react'

import Loading from '../Loading'
import BurgerMenu from './BurgerMenu'
import UserMenu from './components/UserMenu'
import HelpMenu from './components/HelpMenu'
import QuickMenu from './components/QuickMenu'
import TrialCounter from './components/TrialCounter'
import Tooltip from '@components/Tooltip'

import { useGetMyAccount } from '@queries/Account'
import { useHandleClickVendors } from '@helpers/hooks/useHandleClickVendors'
import useWindowSize from '@src/helpers/hooks/useWindowSize'
import { useIsMobile } from '@src/helpers'
import { useHasPermission } from '@helpers/hooks/useHasPermission'
import { needsOnboarding } from '@helpers/hooks/useNeedsOnboarding'
import { Perm } from '@type/permissions'
import { useWindowControlsOverlay } from './utils'
import { useIntercomUnreadCount } from '@helpers/Intercom/useUnreadCount'
import routes from '@src/Routes/routes'
import useStore from '@src/store'
import { useGetTeamSubscription } from '@queries/Billing'
import { usePopupHandlers } from '../Popup'
import PlansListPopup from '@pages/Billing/PlansListPopup'

import styles from './index.module.css'
import MaintenaceBanner from './components/MaintenanceBanner'
import { SHOW_MAINTENANCE_BANNER } from '@src/constants'
import { showsUpgradeBanner } from '@helpers/subscription'
import LinkButton from '../LinkButton'

interface NavigationProps {
  children?: React.ReactNode
  isCollapsed: boolean
  setIsCollapsed: (isCollapsed: boolean) => void
}

const Navigation: React.FC<NavigationProps> = ({ children, isCollapsed, setIsCollapsed }) => {
  const { t } = useTranslation('common')
  // The dot now means "support replied to you", not "you have not clicked
  // this button yet". The old localStorage flag fired once for the lifetime
  // of the browser profile, so a single accidental click silenced the only
  // attention-drawing element in the top bar permanently — including for
  // every real reply that came afterwards.
  const unreadCount = useIntercomUnreadCount()
  const showRedDot = unreadCount > 0
  const { data: user } = useGetMyAccount()
  const canManageBilling = useHasPermission(Perm.BILLING_MANAGE)
  const { data: subscription } = useGetTeamSubscription({ enabled: !!user?.team?.subscription })
  const isWindowControlsOverlay = useWindowControlsOverlay()
  const { setIsPopupOpen, isPopupOpen } = usePopupHandlers()

  const isOnboarding = useStore.use.isOnboarding()

  const isMobile = useIsMobile()
  const { width } = useWindowSize()
  const BURGER_BREAKPOINT = 768
  const hasAccess = !needsOnboarding(user)
  const { clickVendorState } = useHandleClickVendors()

  // Mirror the unread count onto the PWA app badge. Previously this was set
  // once on first load and cleared on the button's first click, so it tracked
  // the same "have you clicked" flag rather than anything unread.
  //
  // Gated on hasAccess too: the badge is OS-level and outlives the page, so on
  // logout or loss of access it would otherwise sit on the installed app icon
  // advertising unread messages for an account that is no longer signed in.
  useEffect(() => {
    if (hasAccess && unreadCount > 0) navigator?.setAppBadge?.(unreadCount)
    else navigator?.clearAppBadge?.()
  }, [unreadCount, hasAccess])

  const handleChatButtonClick = () => {
    // Nothing to clear: the dot and the badge both follow Intercom's unread
    // count, which drops to zero on its own once the conversation is read.
    //
    // Opening is handled by Intercom's custom_launcher_selector binding to
    // #intercom-chat (set in use3rdParties). Do NOT also call Intercom('show')
    // here — that double-fires with the launcher toggle and the chat won't open.
  }

  return (
    <>
      {isPopupOpen && <PlansListPopup isPopupVisible={isPopupOpen} setIsPopupVisible={setIsPopupOpen} />}
      <Loading
        loading={clickVendorState.isLoading}
        redirectTo={clickVendorState.redirectTo}
        redirectURL={clickVendorState.redirectURL}
      />
      {/* Trial: countdown banner. Free: upgrade nudge. Paying plans: nothing.
          Layout mirrors these conditions for banner spacing — keep in sync. */}
      {hasAccess && !isOnboarding && showsUpgradeBanner(subscription) && <TrialCounter />}
      {SHOW_MAINTENANCE_BANNER && !isOnboarding && !(hasAccess && showsUpgradeBanner(subscription)) && (
        <MaintenaceBanner />
      )}
      {hasAccess && (
        <div
          id="navigation"
          // it works only with z-index equal or more 10billon
          className={cx(styles.navPanel, { 'z-onboarding': isOnboarding, 'z-10': !isOnboarding })}
          onContextMenu={(e) => e.preventDefault()}
        >
          {!isMobile && (
            <>
              <Tooltip offset={10} id="tooltip_chat" place="bottom" />
            </>
          )}
          <div id="start" className={cx('flex gap-5 items-center', styles.startNav)}>
            {isMobile || width < BURGER_BREAKPOINT ? (
              !isWindowControlsOverlay && <BurgerMenu isDisabled={!hasAccess} />
            ) : (
              <div
                className={cx(styles.burgerButtonWrap, 'relative group')}
                onClick={() => setIsCollapsed(!isCollapsed)}
              >
                <button
                  className={cx(
                    'group-hover:opacity-10 group-active:opacity-20 group-active:outline cursor-default',
                    styles.burgerButton,
                  )}
                />
                <IconMenu2 className="relative z-10 h-6 w-6 text-black cursor-pointer" stroke={1.5} />
              </div>
            )}
            <NavLink
              to={routes.private.dashboard}
              className={cx(styles.companyLogoWrap, 'hidden xs:inline-block active:outline outline-[3px]', {
                'pointer-events-none': isOnboarding,
              })}
            >
              <img className={styles.companyLogo} alt="Approveit" src="/logo.svg" />
              Approveit
            </NavLink>
            {hasAccess && !isOnboarding && subscription?.is_trial && canManageBilling && (
              <LinkButton
                type="outlined"
                className="text-sm sm:text-base px-1 sm:px-4 border-2"
                color="primary"
                to="https://approveit.today/pricing"
                target="_blank"
              >
                {t('trialCounter.upgradeLabel')} <IconRocket className="h-5 w-5" />
              </LinkButton>
            )}
          </div>
          <div id="end" className={cx('flex gap-1.5', styles.endNav)}>
            <QuickMenu />
            <button
              className={cx(
                styles.navLinkButtonWrap,
                styles.chatButton,
                showRedDot && styles.showRedDot,
                'active:scale-95',
              )}
              data-tooltip-content={t('navigation.chat')}
              data-tooltip-id="tooltip_chat"
              id="intercom-chat"
              // Icon-only, so the tooltip alone is not an accessible name.
              aria-label={t('navigation.chat')}
              onClick={handleChatButtonClick}
            >
              <IconMessage2 className={styles.endMenuIcon} stroke={1.5} />
            </button>
            <HelpMenu />
            <UserMenu />
          </div>
        </div>
      )}
      {children}
    </>
  )
}

export default Navigation
